'use client';
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocale } from 'use-intl';
import { useMedia } from '@ssv-website/hooks';
import styles from './PublicBanner.module.css';

interface PublicBannerProps {
  data?: unknown;
  selectedYear?: number;
  years?: number[];
  onYearChange?: (year: number) => void;
  availableSections?: PublicBannerTabId[];
}

const PUBLIC_BANNER_TABS = [
  { id: 'guests', label: { zh: '嘉宾', en: 'GUESTS' } },
  { id: 'agenda', label: { zh: '议程', en: 'EVENT AGENDA' } },
  { id: 'ai-for-good', label: { zh: 'AI向善产品', en: 'AI FOR GOOD PRODUCTS' } },
  { id: 'exhibition', label: { zh: '展区', en: 'EXHIBITION AREA' } },
  { id: 'videos', label: { zh: '视频', en: 'VIDEOS' } },
  { id: 'moments', label: { zh: '瞬间', en: 'GALLERY' } },
  // { id: 'news', label: { zh: '大会资讯', en: 'News' } },
  // { id: 'reports', label: { zh: '行业报告', en: 'Reports' } },
  // { id: 'partners', label: { zh: '合作伙伴', en: 'Partners' } },
] as const;

export type PublicBannerTabId = (typeof PUBLIC_BANNER_TABS)[number]['id'];

const PC_NAVBAR_HEIGHT = 64;
const PROGRAMMATIC_SCROLL_END_DELAY = 120;
const NAV_SCROLL_REVEAL_GAP = 12;
const MOBILE_NAV_SCROLL_EXTRA_REVEAL = 16;

const getAbsoluteTop = (element: Element) =>
  element.getBoundingClientRect().top + window.pageYOffset;

/**
 * 公共导航 Banner - 底部固定导航栏
 */
export default function PublicBanner({
  data,
  selectedYear = new Date().getFullYear(),
  years = [],
  onYearChange,
  availableSections,
}: PublicBannerProps) {
  const locale = useLocale();
  const isZh = locale === 'zh';
  const isMobile = useMedia('mobile');
  const [activeTab, setActiveTab] = useState('');
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showYearSheet, setShowYearSheet] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navScrollRef = useRef<HTMLDivElement>(null);
  const yearSelectorRef = useRef<HTMLDivElement>(null);
  const [mobileNavScrollState, setMobileNavScrollState] = useState({
    isOverflowing: false,
    showFade: false,
  });
  // 点击 tab 触发的滚动期间，锁定 activeTab，避免 handleScroll 在中间过程判定到上一节
  const isProgrammaticScrollRef = useRef(false);
  const programmaticScrollTimerRef = useRef<number>();

  const getAnchorOffset = useCallback(() => {
    const publicBanner = containerRef.current;
    if (!publicBanner) {
      return (isMobile ? 0 : PC_NAVBAR_HEIGHT) + (isMobile ? 48 : 72);
    }

    const rect = publicBanner.getBoundingClientRect();
    const computedTop = Number.parseFloat(
      window.getComputedStyle(publicBanner).top
    );
    const stickyTop = Number.isFinite(computedTop)
      ? computedTop
      : Math.max(rect.top, 0);

    return stickyTop + rect.height;
  }, [isMobile]);

  const yearList = years.length ? years : [selectedYear];
  const visibleTabs = useMemo(
    () =>
      availableSections
        ? PUBLIC_BANNER_TABS.filter((tab) => availableSections.includes(tab.id))
        : PUBLIC_BANNER_TABS,
    [availableSections]
  );

  const syncMobileNavScrollState = useCallback(() => {
    const navListEl = navScrollRef.current;
    if (!navListEl) {
      return {
        isOverflowing: false,
        showFade: false,
      };
    }

    const nextState = {
      isOverflowing: navListEl.scrollWidth - navListEl.clientWidth > 1,
      showFade:
        navListEl.scrollWidth - (navListEl.scrollLeft + navListEl.clientWidth) >
        1,
    };

    setMobileNavScrollState((prev) =>
      prev.isOverflowing === nextState.isOverflowing &&
      prev.showFade === nextState.showFade
        ? prev
        : nextState
    );
    return nextState;
  }, []);

  const centerNavButton = useCallback(
    (id: string, behavior: ScrollBehavior = 'smooth') => {
      if (!isMobile) return;
      const navListEl = navScrollRef.current;
      const buttonEl = document.querySelector<HTMLButtonElement>(
        `[data-banner-nav-button='${id}']`
      );
      if (!navListEl || !buttonEl) return;

      const tabLeft = buttonEl.offsetLeft;
      const tabRight = tabLeft + buttonEl.offsetWidth;
      const visibleLeft = navListEl.scrollLeft;
      const visibleRight = visibleLeft + navListEl.clientWidth;
      const mobileRevealGap =
        NAV_SCROLL_REVEAL_GAP + MOBILE_NAV_SCROLL_EXTRA_REVEAL;
      const activeTabIndex = visibleTabs.findIndex((tab) => tab.id === id);
      const hasNextTab =
        activeTabIndex >= 0 && activeTabIndex < visibleTabs.length - 1;

      if (hasNextTab) {
        navListEl.scrollTo({
          left: Math.max(
            buttonEl.offsetLeft -
              navListEl.clientWidth / 2 +
              buttonEl.clientWidth / 2,
            0
          ),
          behavior,
        });
        syncMobileNavScrollState();
        return;
      }

      if (tabRight > visibleRight - mobileRevealGap) {
        navListEl.scrollTo({
          left: tabRight - navListEl.clientWidth + mobileRevealGap,
          behavior,
        });
        syncMobileNavScrollState();
        return;
      }

      if (tabLeft < visibleLeft + mobileRevealGap) {
        navListEl.scrollTo({
          left: Math.max(tabLeft - mobileRevealGap, 0),
          behavior,
        });
        syncMobileNavScrollState();
        return;
      }

      syncMobileNavScrollState();
    },
    [isMobile, syncMobileNavScrollState, visibleTabs]
  );

  const handleTabClick = (id: string) => {
    setActiveTab(id);
    const element = document.getElementById(id);
    if (element) {
      const offsetPosition = getAbsoluteTop(element) - getAnchorOffset();

      // 平滑滚动期间以点击项为准，滚动停止后再交给 handleScroll。
      isProgrammaticScrollRef.current = true;
      window.clearTimeout(programmaticScrollTimerRef.current);
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      // 移动端：把激活的 navButton 滚到 navList 中央，避免两侧文字被边缘裁切
      centerNavButton(id);
    }
  };

  const handleYearChange = (year: number) => {
    setShowYearDropdown(false);
    setShowYearSheet(false);
    onYearChange?.(year);
  };

  const handleYearButtonClick = () => {
    if (isMobile) {
      setShowYearSheet(true);
      return;
    }
    setShowYearDropdown((prev) => !prev);
  };

  // 移动端年份弹窗：禁止 body 滚动
  useEffect(() => {
    if (!showYearSheet) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [showYearSheet]);

  useEffect(() => {
    if (!activeTab) return;
    centerNavButton(activeTab);
  }, [activeTab, centerNavButton]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      syncMobileNavScrollState();
    });
    window.addEventListener('resize', syncMobileNavScrollState);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', syncMobileNavScrollState);
    };
  }, [syncMobileNavScrollState, visibleTabs.length]);

  useEffect(() => {
    if (activeTab && !visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab('');
    }
  }, [activeTab, visibleTabs]);

  useEffect(() => {
    if (isMobile || !showYearDropdown) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target && yearSelectorRef.current?.contains(target)) {
        return;
      }
      setShowYearDropdown(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isMobile, showYearDropdown]);

  const handleYearSelectorBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (isMobile || !showYearDropdown) {
      return;
    }

    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && yearSelectorRef.current?.contains(nextTarget)) {
      return;
    }
    setShowYearDropdown(false);
  };

  // 滚动监听，自动更新 activeTab / isSticky
  useEffect(() => {
    const handleScroll = () => {
      const anchorOffset = getAnchorOffset();
      const scrollPosition = window.scrollY + anchorOffset;

      // 找到当前滚动位置对应的 Section
      for (let i = visibleTabs.length - 1; i >= 0; i--) {
        const element = document.getElementById(visibleTabs[i].id);
        if (element) {
          if (scrollPosition >= getAbsoluteTop(element)) {
            // 程序触发的滚动期间，保持点击时设置的 activeTab 不被中间过程覆盖
            if (!isProgrammaticScrollRef.current) {
              setActiveTab(visibleTabs[i].id);
            }
            break;
          }
        }
      }

      // 真正吸顶后再切换背景，避免默认透明态被过早覆盖。
      const stickyTop = Math.max(
        anchorOffset - (containerRef.current?.offsetHeight || 0),
        0
      );
      const bannerTop = containerRef.current?.getBoundingClientRect().top;
      setIsSticky(typeof bannerTop === 'number' && bannerTop <= stickyTop);

      if (isProgrammaticScrollRef.current) {
        window.clearTimeout(programmaticScrollTimerRef.current);
        programmaticScrollTimerRef.current = window.setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, PROGRAMMATIC_SCROLL_END_DELAY);
      }
    };

    // 初始化时执行一次
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.clearTimeout(programmaticScrollTimerRef.current);
    };
  }, [getAnchorOffset, visibleTabs]);

  // 没有任何可跳转模块时，隐藏公共年份和锚点导航，避免留下空导航条。
  if (!visibleTabs.length) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      data-public-banner
      className={`${styles.container} ${
        isSticky ? styles.containerSticky : ''
      } ${isZh ? styles.domesticContainer : ''}`}
    >
      <div className={styles.inner}>
        {/* 左侧导航 */}
        <div className={styles.navWrap}>
          <div
            ref={navScrollRef}
            className={styles.navList}
            data-banner-nav
            onScroll={syncMobileNavScrollState}
          >
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                data-banner-nav-button={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`${styles.navButton} ${
                  activeTab === tab.id ? styles.navButtonActive : ''
                }`}
              >
                {isZh ? tab.label.zh : tab.label.en}
              </button>
            ))}
          </div>
          {isMobile &&
          mobileNavScrollState.isOverflowing &&
          mobileNavScrollState.showFade ? (
            <div className={styles.navFade} aria-hidden="true" />
          ) : null}
        </div>

        {/* 右侧年份选择器 */}
        <div
          ref={yearSelectorRef}
          className={styles.yearSelector}
          onBlur={handleYearSelectorBlur}
        >
          <button
            onClick={handleYearButtonClick}
            className={styles.yearButton}
            aria-haspopup="listbox"
            aria-expanded={showYearDropdown}
          >
            {selectedYear}
            <svg
              className={`${styles.yearIcon} ${
                showYearDropdown ? styles.yearIconOpen : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* 下拉菜单 */}
          {showYearDropdown && (
            <div className={styles.dropdown}>
              {yearList.map((year) => (
                <button
                  key={year}
                  onClick={() => handleYearChange(year)}
                  className={`${styles.dropdownItem} ${
                    year === selectedYear ? styles.dropdownItemActive : ''
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 移动端年份底部弹窗 */}
      {isMobile &&
        showYearSheet &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className={styles.sheetMask}
            onClick={() => setShowYearSheet(false)}
          >
            <div
              className={styles.sheet}
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.sheetTitle}>
                {isZh ? '选择年份' : 'Select Year'}
              </div>
              <div className={styles.sheetList}>
                {yearList.map((year) => {
                  const isActive = year === selectedYear;
                  return (
                    <button
                      key={year}
                      onClick={() => handleYearChange(year)}
                      className={`${styles.sheetItem} ${
                        isActive ? styles.sheetItemActive : ''
                      }`}
                    >
                      <span className={styles.sheetItemLabel}>{year}</span>
                      {isActive && (
                        <svg
                          className={styles.sheetItemIcon}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M5 12l5 5L20 7" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
